import { AxiosError } from "axios";
import { Factory, Loader2 } from "lucide-react";
import { FormEvent, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSocialRegister } from "~/api/authentication/authentication";
import { OrganizationType } from "~/api/model";
import { SocialRegister403 } from "~/api/model/socialRegister403";
import { SocialRegister403Code } from "~/api/model/socialRegister403Code";
import { SocialRegisterBodyIndustry } from "~/api/model/socialRegisterBodyIndustry";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useAuthStepImage } from "~/features/auth/hooks/useAuthStepImage";
import { getStoredAccessToken } from "~/helpers";
import { useThemedOnboardingImage } from "~/hooks/useThemedOnboardingImage";
import { trackEvent } from "~/utils/analytics";
import { CALENDLY_DEMO_URL } from "~/utils/auth/socialAuth";
import logger from "~/utils/logger";

const HTTP_STATUS_FORBIDDEN = 403;

enum REGISTER_FINISH_STEP {
  COMPANY_NAME = 1,
  COMPANY_TYPE = 2,
}

const GENERIC_INDUSTRY_KEY = "GENERIC";

const COMPANY_TYPES = [
  {
    value: OrganizationType.CLINICAL_SPECIALISTS,
    label: "Physicians / Assessors",
    description:
      "Medical assessments, specialist consultations, and clinical evaluations",
    tags: [
      "Assessor Reports",
      "Consultation Reports",
      "Clinical Notes",
      "Diagnostic Reports",
      "Medical Evaluations",
    ],
  },
  {
    value: OrganizationType.IME,
    label: "Independent Assessment (IME, IE)",
    description:
      "Independent medical examinations, insurance assessments, and claims evaluation",
    tags: [
      "IME Reports",
      "Functional Assessments",
      "Vocational Reports",
      "CAT Reports",
      "Treatment Plans",
    ],
  },
  {
    value: OrganizationType.UNDERWRITING,
    label: "Insurance",
    description:
      "Insurance policies, claims processing, risk assessments, and underwriting",
    tags: [
      "Policy Documents",
      "Claims Files",
      "Risk Assessments",
      "Underwriting Reports",
      "Coverage Analysis",
    ],
  },
  {
    value: GENERIC_INDUSTRY_KEY,
    label: "Other",
    description: "General document management for any industry",
    tags: ["Documents", "Reports", "Contracts", "Correspondence", "Records"],
  },
] as const;

type CompanyTypeValue = (typeof COMPANY_TYPES)[number]["value"];

interface FormErrors {
  companyName?: boolean;
  companyType?: boolean;
  generic?: string;
}

export const RegisterFinishPage = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const skipDemo = searchParams.get("skip-demo") === "1";
  const [companyName, setCompanyName] = useState("");
  const [companyType, setCompanyType] = useState<CompanyTypeValue | undefined>(
    OrganizationType.IME
  );
  const [currentStep, setCurrentStep] = useState<REGISTER_FINISH_STEP>(
    REGISTER_FINISH_STEP.COMPANY_NAME
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const { mutate: socialRegister, isPending } =
    useSocialRegister<AxiosError<SocialRegister403>>();

  const companyNameBg = useThemedOnboardingImage("company-name");

  const stepImage = useMemo(() => {
    return companyNameBg;
  }, [companyNameBg]);

  useAuthStepImage(stepImage);

  const validateCompanyNameStep = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!companyName.trim()) {
      newErrors.companyName = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateCompanyTypeStep = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!companyType) {
      newErrors.companyType = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const submitRegistration = () => {
    setErrors({});

    if (!getStoredAccessToken()) {
      setErrors({ generic: "Session expired. Please sign in again." });
      return;
    }

    trackEvent({ eventName: "app/auth/register_finish.submit" });

    socialRegister(
      {
        data: {
          companyName: companyName.trim(),
          industry:
            companyType === GENERIC_INDUSTRY_KEY
              ? SocialRegisterBodyIndustry.IME
              : companyType,
        },
        params: { nodemo: skipDemo },
      },
      {
        onSuccess: () => {
          trackEvent({ eventName: "app/auth/register_finish.success" });
          navigate("/");
        },
        onError: (error) => {
          const { status, data } = error.response ?? {};

          if (
            status === HTTP_STATUS_FORBIDDEN &&
            data?.code === SocialRegister403Code.PUBLIC_DOMAIN_RESTRICTED
          ) {
            trackEvent({
              eventName: "app/auth/register_finish.public_domain_blocked",
            });
            window.location.href = CALENDLY_DEMO_URL;
            return;
          }

          logger.error("Registration finish failed", {
            status,
            code: data?.code,
          });
          trackEvent({ eventName: "app/auth/register_finish.error" });
          setErrors({
            generic: data?.message || "Registration failed. Please try again.",
          });
        },
      }
    );
  };

  const handleContinue = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    if (currentStep === REGISTER_FINISH_STEP.COMPANY_NAME) {
      if (validateCompanyNameStep()) {
        setCurrentStep(REGISTER_FINISH_STEP.COMPANY_TYPE);
      }
    } else if (currentStep === REGISTER_FINISH_STEP.COMPANY_TYPE) {
      if (validateCompanyTypeStep()) {
        submitRegistration();
      }
    }
  };

  return (
    <div className="flex justify-center items-start">
      <div className="flex flex-col gap-4 sm:gap-6 w-full max-w-[560px]">
        <Factory className="h-[32px] w-[32px] text-[#71717A]" />
        <div>
          <h3 className="text-2xl font-semibold text-base-foreground pt-2 sm:pt-[16px]">
            {currentStep === REGISTER_FINISH_STEP.COMPANY_NAME ? (
              <FormattedMessage
                id="auth.register.companyDetails"
                defaultMessage="Organization"
              />
            ) : (
              <FormattedMessage
                id="auth.register.companyDetailsType"
                defaultMessage="Industry"
              />
            )}
          </h3>
          <p className="text-sm text-[#71717A] mt-[16px]">
            {currentStep === REGISTER_FINISH_STEP.COMPANY_NAME ? (
              <FormattedMessage
                id="auth.register.companyDetailsSubtitle"
                defaultMessage="This helps us personalize your workspace and reports."
              />
            ) : (
              <FormattedMessage
                id="auth.register.companyDetailsTypeSubtitle"
                defaultMessage="What kinds of documents do you usually deal with at work?"
              />
            )}
          </p>
        </div>
        <form
          onSubmit={handleContinue}
          className="w-full flex flex-col gap-[24px] text-sm"
        >
          {currentStep === REGISTER_FINISH_STEP.COMPANY_NAME ? (
            <>
              <div className="flex flex-col gap-2">
                <Label
                  htmlFor="companyName"
                  className="block font-medium text-base-foreground"
                >
                  <FormattedMessage
                    id="auth.register.companyName"
                    defaultMessage="Organization Name"
                  />
                </Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: "auth.register.companyNamePlaceholder",
                    defaultMessage: "Enter organization name",
                  })}
                  value={companyName}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 ${
                    errors.companyName ? "border-red-600" : "border-gray-300"
                  }`}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                  }}
                />
                {errors.companyName && (
                  <span className="text-sm text-red-600">
                    <FormattedMessage
                      id="auth.register.companyNameError"
                      defaultMessage="Organization name is required"
                    />
                  </span>
                )}
                {errors.generic && (
                  <span className="text-sm text-red-600">{errors.generic}</span>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto p-1">
                  {COMPANY_TYPES.map((type) => (
                    <div
                      key={type.value}
                      onClick={() => setCompanyType(type.value)}
                      className={`
                        relative flex cursor-pointer rounded-lg border p-3 sm:p-4 transition-all
                        ${
                          companyType === type.value
                            ? "border-[#60A5FA] ring-1 ring-[#60A5FA] bg-sky-50 dark:bg-sky-950"
                            : "border-border hover:border-border/80 hover:bg-accent bg-background"
                        }
                      `}
                    >
                      <div className="flex flex-col sm:flex-row flex-1 gap-2 sm:gap-4">
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="block text-sm font-medium text-foreground">
                            <FormattedMessage
                              id={`auth.register.companyType.${type.value}`}
                              defaultMessage={type.label}
                            />
                          </span>
                          <span className="mt-1 sm:mt-[6px] text-xs text-muted-foreground">
                            <FormattedMessage
                              id={`auth.register.companyType.${type.value}.description`}
                              defaultMessage={type.description}
                            />
                          </span>
                        </div>
                        <div className="hidden sm:flex flex-wrap gap-y-[6px] gap-x-[4px] justify-start items-start max-w-[256px]">
                          {type.tags.slice(0, 5).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center rounded-full bg-muted px-2 py-[2px] text-xs text-foreground"
                            >
                              <FormattedMessage
                                id={`auth.register.companyType.${type.value}.tag.${index}`}
                                defaultMessage={tag}
                              />
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.companyType && (
                  <span className="text-sm text-red-600">
                    <FormattedMessage
                      id="auth.register.companyTypeError"
                      defaultMessage="Please select your industry"
                    />
                  </span>
                )}
                {errors.generic && (
                  <span className="text-sm text-red-600">{errors.generic}</span>
                )}
              </div>
            </>
          )}
          {currentStep === REGISTER_FINISH_STEP.COMPANY_NAME ? (
            <Button>
              <FormattedMessage
                id="auth.register.continue"
                defaultMessage="Continue"
              />
            </Button>
          ) : (
            <Button disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <FormattedMessage
                id={
                  isPending
                    ? "auth.register.creatingAccount"
                    : "auth.register.createAccount"
                }
                defaultMessage={
                  isPending ? "Creating Account..." : "Create Account"
                }
              />
            </Button>
          )}
        </form>
      </div>
    </div>
  );
};
